import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createBrowserRouter, Navigate } from "react-router";
import { HelmetProvider } from "react-helmet-async";
import axios from "axios";
import Cookies from "js-cookie";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import reportWebVitals from "./reportWebVitals";
import "./index.css";
import "services/AppInsightsService";

import App from "App";
import ErrorPage from "components/exception/ErrorPage";
import { ThemeProvider } from "components/shared/ThemeProvider";
import Home from "components/main/Home";
import InactiveAccount, {
    action as inactiveAccountAction,
} from "components/account/InactiveAccount";
import SignIn from "components/account/SignIn";
import { action as oauthSignInAction } from "components/account/OAuthSignIn";
import EmailSignIn, {
    action as emailSignInAction,
} from "components/account/EmailSignIn";
import SignUp, { action as signUpAction } from "components/account/SignUp";
import ResetPassword, {
    action as resetPasswordAction,
} from "components/account/ResetPassword";
import ForgotPassword, {
    action as forgotPasswordAction,
} from "components/account/ForgotPassword";
import AuthSuccess from "components/account/AuthSuccess";
import Dashboard, {
    loader as dashboardLoader,
} from "components/main/Dashboard";
import WishlistListView, {
    loader as wishlistSummaryLoader,
    action as wishlistSummaryAction,
} from "components/wishlist/ListView";
import EditDetailForm, {
    loader as wishlistRequestLoader,
    action as wishlistRequestAction,
} from "components/wishlist/EditDetail";
import ViewDetailForm from "components/wishlist/ViewDetail";
import UserProfile, {
    loader as userProfileLoader,
    action as userProfileAction,
} from "components/account/UserProfile";
import Pricing, {
    loader_for_anonymous_user as pricingLoaderForAnonymousUser,
    loader_for_login_user as pricingLoaderForLoginUser,
} from "components/subscription/Pricing";
import Checkout, {
    loader as checkoutLoader,
    action as checkoutAction,
} from "components/subscription/Checkout";
import ChangePassword, {
    action as changePasswordAction,
} from "components/account/ChangePassword";
import { AuthProvider } from "components/shared/AuthProvider";
import { ConnectionProvider } from "components/shared/ConnectionProvider";
import { PrivateRoute, PublicRoute } from "components/shared/Routes";
import Config from "Config";
import CheckoutCallback, {
    action as checkoutCallbackAction,
} from "components/subscription/CheckoutCallback";
import CheckoutSuccess from "components/subscription/CheckoutSuccess";
import CheckoutCancel from "components/subscription/CheckoutCancel";
import DeepLinkHandler from "components/subscription/DeepLinkHandler";
import SignInException from "components/account/SignInException";
import { Support } from "components/help/Support";
import { McpSetup } from "components/help/McpSetup";
import PrivacyPolicy from "components/legal/PrivacyPolicy";
import { extractUrlFromText } from "utils/Conversion";
import ContainerLayout from "components/layout/ContaineLayout";
import LearnMore from "components/main/LearnMore";
import { useLocalStorage } from "utils/LocalStorage";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";

// Setup axios default config and interceptors
axios.defaults.baseURL = Config.getApiUrl();
axios.defaults.withCredentials = true;
if (process.env.NODE_ENV === "production") {
    axios.defaults.timeout = 10000;
}
axios.interceptors.request.use((config) => {
    const token = sessionStorage.getItem("csrf_token");

    if (token) {
        config.headers["X-CSRFToken"] = token;
    }

    // add x-referer if platform is ios or android
    if (Capacitor.isNativePlatform()) {
        config.headers["x-referer"] = process.env.REACT_APP_API_BASE_URL;
    }
    return config;
});

const get_csrf_token_response = (response) => {
    const csrf_token = response.headers.get("X-CSRF-Token");
    if (csrf_token) {
        sessionStorage.setItem("csrf_token", csrf_token);
    }
    return response;
};

axios.interceptors.response.use(get_csrf_token_response, (error) => {
    // if backend return 401 unauthorised error, try refresh the access token
    if (error.response?.status === 401 && !error.config.__isRetryRequest) {
        const originalRequest = error.config;
        originalRequest._retry = true;
        return fetch(`${Config.getApiUrl()}account/token/extend`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "include",
        }).then(async (response) => {
            get_csrf_token_response(response);

            if (response.ok) {
                const axiosinstance = axios.create();
                return axiosinstance(originalRequest);
            }

            Cookies.remove("auth_data");
            return Promise.reject(error);
        });
    }

    if (error.response) {
        get_csrf_token_response(error.response);
    }

    return Promise.reject(error);
});

// event listener
window.addEventListener("sharedData", (event) => {
    let { hash, product_url, product_name, image_url } = event.detail;

    // Extract first URL from text and overwrite url variable if found
    if (typeof product_url !== "string" && typeof product_name === "string") {
        const urlMatch = extractUrlFromText(product_name);
        if (urlMatch && urlMatch[0]) {
            product_url = urlMatch[0];
        }
    }

    // Store shared data in local storage
    const { setItem } = useLocalStorage();
    const sharedData = {
        hash,
        product_url,
        product_name,
        image_url,
    };
    setItem("wishliz-create-detail-temp", JSON.stringify(sharedData));
});

// Setup react-router
const router = createBrowserRouter([
    {
        element: <App />,
        errorElement: (
            <ThemeProvider>
                <ErrorPage />
            </ThemeProvider>
        ),
        children: [
            { index: true, element: <Home /> },
            { path: "features", element: <LearnMore /> },
            {
                element: <ContainerLayout />,
                children: [
                    // Dashboard & Wishlist
                    {
                        path: "dashboard",
                        element: (
                            <PrivateRoute>
                                <Dashboard />
                            </PrivateRoute>
                        ),
                        loader: dashboardLoader,
                    },
                    {
                        path: "wishlist",
                        element: (
                            <PrivateRoute>
                                <WishlistListView />
                            </PrivateRoute>
                        ),
                        loader: wishlistSummaryLoader,
                        action: wishlistSummaryAction,
                    },
                    {
                        path: "wishlist/create",
                        element: (
                            <PrivateRoute>
                                <EditDetailForm />
                            </PrivateRoute>
                        ),
                        action: wishlistRequestAction,
                    },
                    {
                        path: "wishlist/:wishlistRequestId/edit",
                        element: (
                            <PrivateRoute>
                                <EditDetailForm />
                            </PrivateRoute>
                        ),
                        loader: wishlistRequestLoader,
                        action: wishlistRequestAction,
                    },
                    {
                        path: "wishlist/:wishlistRequestId",
                        element: (
                            <PrivateRoute>
                                <ViewDetailForm />
                            </PrivateRoute>
                        ),
                        loader: wishlistRequestLoader,
                        action: wishlistRequestAction,
                    },
                    // Account
                    {
                        path: "account",
                        element: (
                            <PrivateRoute>
                                <UserProfile />
                            </PrivateRoute>
                        ),
                        loader: userProfileLoader,
                        action: userProfileAction,
                    },
                    {
                        path: "account/signup",
                        element: (
                            <PublicRoute>
                                <SignUp />
                            </PublicRoute>
                        ),
                        action: signUpAction,
                    },
                    {
                        path: "account/signin",
                        element: (
                            <PublicRoute>
                                <SignIn />
                            </PublicRoute>
                        ),
                        action: oauthSignInAction,
                    },
                    {
                        path: "account/signin/email",
                        element: (
                            <PublicRoute>
                                <EmailSignIn />
                            </PublicRoute>
                        ),
                        action: emailSignInAction,
                    },
                    {
                        path: "account/signin-error",
                        element: (
                            <PublicRoute>
                                <SignInException />
                            </PublicRoute>
                        ),
                    },
                    {
                        path: "account/inactive-account",
                        action: inactiveAccountAction,
                        element: (
                            <PrivateRoute>
                                <InactiveAccount />
                            </PrivateRoute>
                        ),
                    },
                    {
                        path: "account/reset-password",
                        action: resetPasswordAction,
                        element: (
                            <PublicRoute>
                                <ResetPassword />
                            </PublicRoute>
                        ),
                    },
                    {
                        path: "account/forgot-password",
                        action: forgotPasswordAction,
                        element: (
                            <PublicRoute>
                                <ForgotPassword />
                            </PublicRoute>
                        ),
                    },
                    {
                        path: "account/change-password",
                        action: changePasswordAction,
                        element: (
                            <PrivateRoute>
                                <ChangePassword />
                            </PrivateRoute>
                        ),
                    },
                    // Subscription
                    {
                        path: "subscription/pricing",
                        element: <Pricing />,
                        loader: pricingLoaderForAnonymousUser,
                    },
                    {
                        path: "subscription/pricing/upgrade",
                        element: (
                            <PrivateRoute>
                                <Pricing />
                            </PrivateRoute>
                        ),
                        loader: pricingLoaderForLoginUser,
                    },
                    {
                        path: "subscription/:platform/:provider/checkout-callback",
                        action: checkoutCallbackAction,
                        element: (
                            <PrivateRoute>
                                <CheckoutCallback />
                            </PrivateRoute>
                        ),
                    },
                    {
                        path: "subscription/:platform/:provider/checkout-success",
                        element: <CheckoutSuccess />,
                    },
                    {
                        path: "subscription/:platform/:provider/checkout-cancel",
                        element: <CheckoutCancel />,
                    },
                    {
                        path: "subscription/checkout/:planId",
                        element: (
                            <PrivateRoute>
                                <Checkout />
                            </PrivateRoute>
                        ),
                        loader: checkoutLoader,
                        action: checkoutAction,
                    },
                    {
                        path: "subscription/o/:provider",
                        element: <DeepLinkHandler />,
                    },
                    {
                        path: "subscription/a/:provider",
                        element: <DeepLinkHandler />,
                    },
                    {
                        path: "subscription/w/:provider",
                        element: <DeepLinkHandler />,
                    },
                    // Misc
                    { path: "help", element: <Support /> },
                    { path: "help/mcp", element: <McpSetup /> },
                    {
                        path: "legal/privacy-policy",
                        element: <PrivacyPolicy />,
                    },
                ],
            },
        ],
    },
    { path: "/*", element: <Navigate to="/" replace /> },
    { path: "/auth-success", element: <AuthSuccess /> },
]);

// Handle deep links in native app
if (Capacitor.isNativePlatform()) {
    CapacitorApp.addListener("appUrlOpen", (event) => {
        const url = new URL(event.url);
        const path = url.pathname + url.search + url.hash;
        // Navigate to the deep linked path
        if (path) {
            router.navigate(path);
        }
    });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
    <React.StrictMode>
        <HelmetProvider>
            <ConnectionProvider>
                <AuthProvider>
                    <RouterProvider router={router} />
                </AuthProvider>
            </ConnectionProvider>
        </HelmetProvider>
    </React.StrictMode>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.register();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
